import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MapPin, Eye, EyeOff } from "lucide-react";

/* ── Fix Leaflet default marker icons (webpack breaks the URLs) ── */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/* ── Avatar DivIcon builder ──────────────────────────────────── */
function avatarIcon(participant, isMe) {
  const border = isMe ? "#00B4D8" : "#121212";
  const opacity = participant.stale ? 0.45 : 1;
  const inner = participant.avatar_url
    ? `<img src="${participant.avatar_url}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" />`
    : `<span style="font-weight:800;font-size:14px;color:#121212;">${(participant.name || "?")[0].toUpperCase()}</span>`;

  return L.divIcon({
    className: "",
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    popupAnchor: [0, -22],
    html: `<div style="
      width:38px;height:38px;border-radius:50%;
      border:3px solid ${border};
      background:#fff;display:flex;align-items:center;justify-content:center;
      overflow:hidden;opacity:${opacity};
      box-shadow:0 2px 8px rgba(0,0,0,.18);
    ">${inner}</div>`,
  });
}

export default function LiveMap() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(false);
  const intervalRef = useRef(null);

  /* ── Fetch live locations ──────────────────────────────────── */
  const fetchLocations = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/activities/${id}/live-locations`);
      setData(d);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load locations");
    }
  }, [id]);

  useEffect(() => {
    fetchLocations();
    intervalRef.current = setInterval(fetchLocations, 15_000);
    return () => clearInterval(intervalRef.current);
  }, [fetchLocations]);

  /* ── Toggle location sharing ───────────────────────────────── */
  const toggleSharing = async (enable) => {
    setToggling(true);
    try {
      await api.patch("/users/me/location-sharing", { enabled: enable });
      if (enable && navigator.geolocation) {
        // Seed first position immediately
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            api.post("/users/me/location", {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            }).catch(() => {});
          },
          () => {},
          { enableHighAccuracy: true }
        );
      }
      await refresh();
      setTimeout(fetchLocations, 1000);
    } catch (e) {
      console.error("Toggle sharing failed:", e);
    } finally {
      setToggling(false);
    }
  };

  /* ── Compute map center ────────────────────────────────────── */
  const getCenter = () => {
    if (data?.meeting_point?.lat != null && data?.meeting_point?.lng != null) {
      return [data.meeting_point.lat, data.meeting_point.lng];
    }
    const located = (data?.participants || []).filter((p) => p.lat != null && p.lng != null);
    if (located.length > 0) {
      const avgLat = located.reduce((s, p) => s + p.lat, 0) / located.length;
      const avgLng = located.reduce((s, p) => s + p.lng, 0) / located.length;
      return [avgLat, avgLng];
    }
    return [59.33, 18.07]; // Stockholm fallback
  };

  /* ── Status label ──────────────────────────────────────────── */
  const statusOf = (p) => {
    if (!p.sharing_enabled) return t("map.locationHidden");
    if (p.lat == null) return t("map.offline");
    if (p.stale) return t("map.offline");
    return t("map.live");
  };

  const statusColor = (p) => {
    const s = statusOf(p);
    if (s === t("map.live")) return "#22C55E";
    if (s === t("map.offline")) return "#9CA3AF";
    return "#9CA3AF";
  };

  const sharing = user?.location_sharing;

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <button onClick={() => nav(-1)} className="btn-nb mb-4"><ArrowLeft className="w-4 h-4" strokeWidth={3} /> {t("common.back")}</button>
        <div className="card-nb p-6 bg-white text-center">
          <p className="text-[#FF6B6B] font-bold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-[#FFFDF7]">
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="px-4 sm:px-6 pt-4 pb-3 border-b-2 border-[#121212] bg-white flex items-center gap-3 z-20 shrink-0">
        <button onClick={() => nav(-1)} className="btn-nb !p-2" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" strokeWidth={3} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black truncate">
            <MapPin className="w-4 h-4 inline -mt-0.5 mr-1" strokeWidth={3} />
            {t("map.liveMap")}
          </div>
        </div>
        {sharing ? (
          <button
            onClick={() => toggleSharing(false)}
            disabled={toggling}
            className="btn-nb !py-1.5 !px-3 text-xs flex items-center gap-1.5"
            data-testid="go-invisible-btn"
          >
            <EyeOff className="w-3.5 h-3.5" strokeWidth={2.5} />
            {t("map.goInvisible")}
          </button>
        ) : (
          <button
            onClick={() => toggleSharing(true)}
            disabled={toggling}
            className="btn-nb btn-nb-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
            data-testid="enable-sharing-btn"
          >
            <Eye className="w-3.5 h-3.5" strokeWidth={2.5} />
            {t("map.shareLocation")}
          </button>
        )}
      </div>

      {/* ── Map ──────────────────────────────────────────────── */}
      <div className="flex-1 relative z-10">
        {data ? (
          <MapContainer
            center={getCenter()}
            zoom={14}
            style={{ height: "100%", width: "100%" }}
            zoomControl={false}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />

            {/* Meeting point */}
            {data.meeting_point?.lat != null && data.meeting_point?.lng != null && (
              <CircleMarker
                center={[data.meeting_point.lat, data.meeting_point.lng]}
                radius={12}
                pathOptions={{ color: "#EF4444", fillColor: "#EF4444", fillOpacity: 0.35, weight: 3 }}
              >
                <Popup>
                  <strong>{t("map.meetingPoint")}</strong>
                  {data.meeting_point.label && <div className="text-xs">{data.meeting_point.label}</div>}
                </Popup>
              </CircleMarker>
            )}

            {/* Participant markers */}
            {(data.participants || [])
              .filter((p) => p.lat != null && p.lng != null)
              .map((p) => (
                <Marker
                  key={p.id}
                  position={[p.lat, p.lng]}
                  icon={avatarIcon(p, p.id === user?.id)}
                >
                  <Popup>
                    <div style={{ textAlign: "center", minWidth: 100 }}>
                      <div style={{ fontWeight: 700 }}>{p.name}{p.is_host ? " ⭐" : ""}</div>
                      {p.stale && <div style={{ fontSize: 11, color: "#9CA3AF" }}>{t("map.lastSeen5Min")}</div>}
                      <Link
                        to={`/u/${p.id}`}
                        style={{ display: "inline-block", marginTop: 4, fontSize: 13, fontWeight: 600, color: "#00B4D8" }}
                      >
                        {t("common.viewProfile")}
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              ))}
          </MapContainer>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="font-display font-bold text-[#7A7A7A]">{t("map.loadingMap")}</div>
          </div>
        )}
      </div>

      {/* ── Participant list ─────────────────────────────────── */}
      {data && (
        <div className="shrink-0 border-t-2 border-[#121212] bg-white max-h-[35vh] overflow-y-auto">
          <div className="px-4 pt-3 pb-1">
            <h3 className="font-display font-black text-sm mb-2">{t("activity.participants")}</h3>
          </div>
          <div className="px-4 pb-4 space-y-2">
            {(data.participants || []).map((p) => (
              <Link
                key={p.id}
                to={`/u/${p.id}`}
                className="flex items-center gap-3 p-2 rounded-xl hover:bg-[#F4F1EA] transition-colors"
                data-testid={`participant-${p.id}`}
              >
                <div className="w-9 h-9 rounded-full bg-[#F4F1EA] border-2 border-[#121212] flex items-center justify-center font-display font-black overflow-hidden shrink-0">
                  {p.avatar_url
                    ? <img src={p.avatar_url} alt="" className="w-full h-full object-cover" />
                    : (p.name || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate flex items-center gap-1">
                    {p.name}
                    {p.is_host && <span className="text-[10px] tag-nb bg-[#F9C349] !py-0">{t("activity.host")}</span>}
                    {p.id === user?.id && <span className="text-[10px] tag-nb bg-[#00B4D8] text-white !py-0">{t("common.you")}</span>}
                  </div>
                </div>
                <span className="text-xs font-bold flex items-center gap-1" style={{ color: statusColor(p) }}>
                  {statusOf(p) === t("map.live") && <span className="w-2 h-2 rounded-full bg-[#22C55E] inline-block animate-pulse" />}
                  {statusOf(p)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
